import { Button, InlineNotification } from '@carbon/react';
import { ArrowRight } from '@carbon/icons-react';
import { FormattedCount } from 'components/formatted-count';
import { AnalysisTile } from '../analysis-tile/analysis-tile';
import { FactorChips } from '../factor-chips/factor-chips';
import type { AnalysisResponse } from '../../types';
import {
  deltaSign,
  formatPercent,
  formatSignedMillions,
  formatSignedPrice,
  millionsDeltaSign,
} from '../../utils';
import styles from './analysis-panel.module.scss';

interface AnalysisPanelProps {
  analysis: AnalysisResponse;
  onProceed: () => void;
}

// Step 2 results: the summary tiles, the changed/matched factor chips, the
// non-viable warning when the two evals share nothing, and the proceed bar. NPV
// attribution (the waterfall) is a later step. The Avg Price Δ tile shows the oil/gas
// realized-price change; the oil/gas EUR sub-metric stays deferred (no volume total yet).
export function AnalysisPanel({ analysis, onProceed }: AnalysisPanelProps) {
  if (!analysis.comparable) {
    return (
      <InlineNotification
        kind="warning"
        lowContrast
        hideCloseButton
        title="No shared assets or slots"
        subtitle="These evaluations have no shared assets or slots and cannot be compared."
      />
    );
  }

  return (
    <div className={styles.panel}>
      <div className={styles.tiles}>
        <AnalysisTile
          label="Shared Assets"
          value={<FormattedCount value={analysis.sharedAssetCount} />}
          sub={
            <>
              +<FormattedCount value={analysis.gainedAssetCount} /> gained · −
              <FormattedCount value={analysis.lostAssetCount} /> lost
            </>
          }
        />
        <AnalysisTile
          label="Shared Slots"
          value={<FormattedCount value={analysis.sharedSlotCount} />}
          sub={
            <>
              +<FormattedCount value={analysis.gainedSlotCount} /> gained · −
              <FormattedCount value={analysis.lostSlotCount} /> lost
            </>
          }
        />
        <AnalysisTile
          label="NPV Delta (Target)"
          value={formatSignedMillions(analysis.npvDeltaTarget)}
          tone={millionsDeltaSign(analysis.npvDeltaTarget)}
          sub={`${formatPercent(analysis.npvDeltaTargetPct)} vs. origin`}
        />
        <AnalysisTile
          label="NPV Delta (Max)"
          value={formatSignedMillions(analysis.npvDeltaMax)}
          tone={millionsDeltaSign(analysis.npvDeltaMax)}
          sub={`${formatPercent(analysis.npvDeltaMaxPct)} vs. origin`}
        />
        <AnalysisTile
          label="Factors Changed"
          value={<FormattedCount value={analysis.factorsChangedCount} />}
          tone="negative"
          sub={
            <>
              of <FormattedCount value={analysis.factorsTotalCount} />{' '}
              comparable
            </>
          }
        />
        <AnalysisTile
          label="Avg Price Δ"
          value={formatSignedPrice(analysis.oilAvgPriceDelta)}
          tone={deltaSign(analysis.oilAvgPriceDelta)}
          sub={`Oil per bbl · Gas ${formatSignedPrice(
            analysis.gasAvgPriceDelta
          )} per mcf`}
        />
      </div>

      <FactorChips factors={analysis.factors} />

      <div className={styles.proceed}>
        <span className={styles.proceedText}>
          The comparison engine will iterate through each changed factor at the
          slot level to attribute value. You can reorder the factor groups
          before running.
        </span>
        <Button kind="primary" renderIcon={ArrowRight} onClick={onProceed}>
          Proceed to Factor Ordering
        </Button>
      </div>
    </div>
  );
}
